import {
  Annotation,
  AnnotationRect,
  EnhancedAnnotation,
  KnownCloud,
  KnownUser,
  UserPrefState,
} from '@readcloud/data';
import C from 'classnames';
import React, { RefObject } from 'react';
import { DraggableEventHandler } from 'react-draggable';
import isEqual from 'react-fast-compare';
import { Rnd, RndResizeCallback } from 'react-rnd';
import { annotationContainers } from '../helpers/createEmptyAnnotations';
import { preventDefault } from '../utils';
import { AnnotationToolbar } from './AnnotationToolbar';
import { AnnotationViewer } from './AnnotationViewer';
const handleStyle = {
  touchAction: 'none',
  borderRadius: '50%',
  border: 'solid 1px #ddd',
};
const resizerBorder = 'dashed 1px grey';
const resizeHandlerStyles: React.ComponentProps<
  typeof Rnd
>['resizeHandleStyles'] = {
  bottomLeft: handleStyle,
  bottomRight: handleStyle,
  topLeft: handleStyle,
  topRight: handleStyle,
  bottom: {
    borderBottom: resizerBorder,
  },
  top: {
    borderTop: resizerBorder,
  },
  left: { borderLeft: resizerBorder },
  right: { borderRight: resizerBorder },
};

export const resizeDisable: React.ComponentProps<typeof Rnd>['enableResizing'] =
  {
    bottom: false,
    bottomRight: false,
    bottomLeft: false,
    left: false,
    right: false,
    top: false,
    topLeft: false,
    topRight: false,
  };

export const scaleRect = (
  scale: number,
  rect: AnnotationRect
): AnnotationRect => ({
  height: scale * rect?.height || 1,
  width: scale * rect?.width || 1,
  x: scale * rect.x || 0,
  y: scale * rect.y || 0,
});

interface Props {
  containerRef: RefObject<HTMLDivElement>;
  outerContainer: HTMLDivElement;
  pagesContainer: HTMLDivElement;
  pageContainer: HTMLDivElement;
  belowContentZIndex: number;
  aboveContentZIndex: number;
  selected: boolean;
  /**
   * The annotation has been clicked/selected
   */
  onSelect: (annotation: Annotation, reference: HTMLElement) => void;
  onChange: (annotation: Annotation) => void;
  onDelete: (id: string) => void;
  onComment: (id: string) => void;
  annotation: EnhancedAnnotation;
  /**
   * Controls if the annotation can be edited
   */
  editable: boolean;
  scale: number;
  /**
   * Known Users is the data that populates the list of people in the sharing management popover
   */
  knownUsers: KnownUser[];
  /**
   * Known Clouds is the data that populates the list of clouds in the sharing management popover
   */
  knownClouds: KnownCloud[];
  /**
   * Can the user declare the annotation as a Publisher type which will cause it to be visible to everyone?
   */
  showPublisherOptions: boolean;
  onSavePreferences?: (type: string, data: Partial<Annotation['data']>) => void;
  annoPrefs?: UserPrefState['annoPrefs'];
}

/**
 * This component is the crux of annotation rendering.
 * - A component that renders an Annotation object
 * - It can move itself from user interaction/dragging etc
 * - It renders an AnnotationToolbar that will become visible when the "selected" prop is true
 */
export const AnnotationRNDComponent: React.FC<Props> = (props) => {
  const minHeight =
    (annotationContainers[props.annotation.type]?.config?.minHeight || 0) *
    props.scale;
  const minWidth =
    (annotationContainers[props.annotation.type]?.config?.minWidth || 0) *
    props.scale;
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [tempRect, setTempRect] = React.useState<AnnotationRect | undefined>();
  const [dragging, setDragging] = React.useState(false);
  const [resizing, setResizing] = React.useState(false);
  const [rndRef, setRndRef] = React.useState<Rnd | null>(null);
  const classes = C(
    !props.editable && props.annotation.data?.belowContent
      ? 'below-content'
      : 'above-content',
    props.editable && 'rc-pointer-events-auto rc-touch-auto'
  );

  /* 
    React.useEffect(() => {
      if (containerRef && props.editable) {
        containerRef.addEventListener("touchstart", preventDefault, {
          passive: false
        });
        containerRef.addEventListener("touchmove", preventDefault, {
          passive: false
        });
        return () => {
          containerRef.removeEventListener("touchstart", preventDefault);
          containerRef.removeEventListener("touchmove", preventDefault);
        };
      }
      return;
    }, [containerRef, props.editable]); */

  React.useEffect(() => {
    const resizable = rndRef && rndRef.resizable && rndRef.resizable.resizable;
    if (resizable && props.editable) {
      resizable.addEventListener('touchstart', preventDefault, {
        passive: false,
      });
      resizable.addEventListener('touchmove', preventDefault, {
        passive: false,
      });
      return () => {
        resizable.removeEventListener('touchstart', preventDefault);
        resizable.removeEventListener('touchmove', preventDefault);
      };
    }
    return;
  }, [rndRef, props.editable]);

  const handleDragStop = React.useCallback<DraggableEventHandler>(() => {
    /* Use temp rect and not the data parameters. For some reason they don't match up */
    if (!props.editable || !dragging) return;
    if (tempRect) {
      const nextRect = {
        ...props.annotation.data?.rect,
        x: tempRect.x / props.scale,
        y: tempRect.y / props.scale,
      };
      if (!isEqual(nextRect, props.annotation.data.rect))
        props.onChange({
          ...props.annotation,
          data: {
            ...props.annotation.data,
            rect: nextRect,
          },
        } as Annotation);
    }
    setDragging(false);
    setTempRect(undefined);
  }, [props.onChange, props.annotation, tempRect, props.scale, props.editable]);
  const handleTempDrag = React.useCallback<DraggableEventHandler>(
    (e, data) => {
      if (!props.editable) return;
      setDragging(true);
      setTempRect({
        width: data.node.offsetWidth,
        height: data.node.offsetHeight,
        x: data.x,
        y: data.y,
      });
    },
    [props.scale, props.editable]
  );
  const handlePointerDown = React.useCallback(
    (
      e:
        | React.PointerEvent<HTMLElement>
        | React.MouseEvent<HTMLElement>
        | React.TouchEvent<HTMLElement>
    ) =>
      props.editable && e.target
        ? props.onSelect(props.annotation, e.currentTarget)
        : undefined,
    [props.annotation.id, props.onSelect, props.editable]
  );

  const handleTempResize = React.useCallback<RndResizeCallback>(
    (e, dir, ref, delta, position) => {
      if (!props.editable) return;
      setResizing(true);
      setTempRect((s) => {
        return {
          ...position,
          width: ref.offsetWidth,
          height: ref.offsetHeight,
        };
      });
    },
    [props.scale, props.editable]
  );

  const handleResizeStop = React.useCallback<RndResizeCallback>(
    (e, dir, ref, delta, position) => {
      if (!props.editable || !resizing) return;
      if (tempRect) {
        const nextRect = scaleRect(1 / props.scale, {
          ...position,
          width: tempRect?.width || 1,
          height: tempRect?.height || 1,
        });
        if (!isEqual(nextRect, props.annotation.data.rect)) {
          props.onChange({
            ...props.annotation,
            data: {
              ...props.annotation.data,
              rect: nextRect,
            },
          } as Annotation);
        }
      }
      setTempRect(undefined);
      setResizing(false);
    },
    [props.onChange, props.annotation, tempRect, props.scale, props.editable]
  );

  return (
    <>
      <AnnotationToolbar
        zIndex={props.aboveContentZIndex}
        showPublisherOptions={props.showPublisherOptions}
        knownClouds={props.knownClouds}
        knownUsers={props.knownUsers}
        onChange={props.onChange}
        onComment={props.onComment}
        onDelete={props.onDelete}
        reference={
          (!resizing &&
            !dragging &&
            props.selected &&
            props.editable &&
            containerRef?.current) ||
          undefined
        }
        annotation={props.annotation}
        onSavePreferences={props.onSavePreferences}
        annoPrefs={props.annoPrefs}
        containerRef={props.containerRef}
        outerContainer={props.outerContainer}
        pagesContainer={props.pagesContainer}
        pageContainer={props.pageContainer}
        scale={props.scale}
      />
      <Rnd
        className={classes}
        disableDragging={!props.editable}
        enableResizing={props.editable ? undefined : resizeDisable}
        resizeHandleStyles={props.selected ? resizeHandlerStyles : undefined}
        onDrag={props.editable ? handleTempDrag : undefined}
        onDragStop={props.editable ? handleDragStop : undefined}
        onResize={props.editable ? handleTempResize : undefined}
        onResizeStop={props.editable ? handleResizeStop : undefined}
        ref={setRndRef}
        bounds="parent"
        minHeight={minHeight}
        minWidth={minWidth}
        size={tempRect || scaleRect(props.scale, props.annotation.data.rect)}
        position={
          tempRect || scaleRect(props.scale, props.annotation.data.rect)
        }
      >
        <AnnotationViewer
          selected={props.selected}
          editing={props.editable}
          onMouseDown={handlePointerDown}
          onTouchStart={handlePointerDown}
          onChange={props.onChange}
          scale={props.scale}
          innerRef={containerRef}
          dragging={props.editable ? dragging : false}
          annotation={props.annotation}
          height={
            tempRect
              ? tempRect.height
              : props.annotation.data.rect.height * props.scale
          }
          width={
            tempRect
              ? tempRect.width
              : props.annotation.data.rect.width * props.scale
          }
        />
      </Rnd>
    </>
  );
};
